import { isSafari, isIOS } from './deprecatedBrowserUtils'

/**
 * Detects if the browser supports CSS container style queries.
 * Works in both browser and SSR contexts.
 *
 * In browser context: Performs actual feature detection
 * In SSR context: Returns true (we assume that container style queries are supported)
 *
 * Note: This uses a cached result to avoid repeated DOM operations.
 *
 * @returns true if container style queries are supported, false otherwise
 */
export const supportsContainerStyleQueries = (): boolean => {
	// Check if we're in a browser environment
	if (typeof window === 'undefined' || typeof document === 'undefined') {
		// SSR context - return true (we assume that container style queries are supported)
		return true
	}

	// Browser context - check if already cached
	if (typeof (globalThis as any).supportsContainerStyleQueriesFlag !== 'undefined') {
		return (globalThis as any).supportsContainerStyleQueriesFlag
	}

	try {
		// Safari does not support container style queries after relayout
		// On SSR first render it should work, but we take a conservative approach
		if (isSafari(window) || isIOS(window)) {
			;(globalThis as any).supportsContainerStyleQueriesFlag = false
			return false
		}

		// Container style query feature detection
		const container = document.createElement('div')
		const child = document.createElement('div')
		const style = document.createElement('style')

		container.style.cssText = '--test:1;position:absolute;top:-9999px;width:0px;height:0px;display:none;'
		container.appendChild(child)

		style.textContent = '@container style(--test:1){div{--detected:yes}}'

		document.head.appendChild(style)
		document.body.appendChild(container)

		const isSupported = getComputedStyle(child).getPropertyValue('--detected').trim() === 'yes'

		// Cleanup
		document.body.removeChild(container)
		document.head.removeChild(style)
		;(globalThis as any).supportsContainerStyleQueriesFlag = isSupported
		return isSupported
	} catch {
		// If detection fails, assume not supported
		;(globalThis as any).supportsContainerStyleQueriesFlag = false
		return false
	}
}
